import React, { useState, useEffect, useRef } from 'react';
import type { Category, ExerciseConfig } from '../../domain/scoring/types';
import { CATEGORY_LABELS } from '../../domain/scoring/categories';
import type { LiftHighlightMap } from '../../domain/workouts/liftHighlights';
import { LiftBests } from '../LiftBests/LiftBests';
import './LiftModal.css';

export interface LiftEntryData {
  exerciseId: string;
  weightLb: number;
  reps: number;
  workoutDate: string;
  note: string;
}

/** The last weight and reps logged for an exercise, keyed by exercise id. */
export type LastEntries = Record<string, { weightLb: number; reps: number }>;

export interface LiftModalProps {
  isOpen: boolean;
  category: Category;
  onClose: () => void;
  onSubmit: (data: LiftEntryData) => void;
  exercises: ExerciseConfig[];
  isSubmitting?: boolean;
  defaultExerciseId?: string;
  /** Seeds the weight and reps fields with whatever was logged last. */
  lastEntries?: LastEntries;
  /** Each exercise's PR and month best, shown under the form. */
  highlights?: LiftHighlightMap;
}

function getLADate(): Date {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
}

function getLADateString(): string {
  const d = getLADate();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Latest date a workout may be logged for. Past dates are allowed (so older
// history can be backfilled); future dates are not.
function getMaxWorkoutDate(): string {
  return getLADateString();
}

export function LiftModal({
  isOpen,
  category,
  onClose,
  onSubmit,
  exercises,
  isSubmitting = false,
  defaultExerciseId,
  lastEntries,
  highlights,
}: LiftModalProps) {
  const [exerciseId, setExerciseId] = useState<string>(
    defaultExerciseId || (exercises.length > 0 ? exercises[0].id : ''),
  );
  const selectedExercise = exercises.find((ex) => ex.id === exerciseId);
  const isBodyWeightLift = (selectedExercise?.bodyWeightFactor ?? 0) > 0;
  // Dumbbell exercises take the weight of ONE dumbbell and double it internally.
  const isPerDumbbell = (selectedExercise?.inputWeightMultiplier ?? 1) > 1;

  /**
   * Weight and reps hold what the user typed, or null while the field is still
   * showing the seed from the last time this lift was logged. Derived rather than
   * stored so switching exercise reseeds without an effect racing the typing.
   */
  const [typedWeight, setTypedWeight] = useState<string | null>(null);
  const [typedReps, setTypedReps] = useState<string | null>(null);
  const previous = lastEntries?.[exerciseId];
  const weightLb = typedWeight ?? (previous ? String(previous.weightLb) : '');
  const reps = typedReps ?? String(previous?.reps ?? 1);

  const selectedHighlights = highlights?.[exerciseId];

  const [workoutDate, setWorkoutDate] = useState<string>(getLADateString());

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const modalRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Handle focus trap
  useEffect(() => {
    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !isOpen || !modalRef.current) return;

      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    };

    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  // Form defaults are set via initial useState values. Parent remounts
  // this component via key={selectedCategory} to reset all state.

  if (!isOpen) return null;

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    const weightNum = parseInt(weightLb, 10);
    const repsNum = parseInt(reps, 10);

    if (!exerciseId) {
      newErrors.exerciseId = 'Exercise is required';
    }

    if (isBodyWeightLift) {
      // Added weight is optional for bodyweight lifts; blank counts as 0.
      if (weightLb !== '' && (isNaN(weightNum) || weightNum < 0 || weightNum > 2000)) {
        newErrors.weightLb = 'Added weight must be an integer between 0 and 2000';
      }
    } else if (!weightLb || isNaN(weightNum) || weightNum < 1 || weightNum > 2000) {
      newErrors.weightLb = 'Weight must be an integer between 1 and 2000';
    }

    if (!reps || isNaN(repsNum) || repsNum < 1 || repsNum > 10) {
      newErrors.reps = 'Reps must be an integer between 1 and 10';
    }

    if (!workoutDate) {
      newErrors.workoutDate = 'Date is required';
    } else if (workoutDate > getMaxWorkoutDate()) {
      newErrors.workoutDate = 'Date cannot be in the future';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({
        exerciseId,
        weightLb: weightLb === '' ? 0 : parseInt(weightLb, 10),
        reps: parseInt(reps, 10),
        workoutDate,
        note: '',
      });
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const maxDate = getMaxWorkoutDate();

  return (
    <div
      className="lift-modal__overlay"
      onClick={handleOverlayClick}
      aria-modal="true"
      role="dialog"
      aria-labelledby="lift-modal-title"
    >
      <div className="lift-modal__container" ref={modalRef}>
        <div className="lift-modal__handle" />

        <button
          className="lift-modal__close"
          onClick={onClose}
          aria-label="Close modal"
          type="button"
        >
          &times;
        </button>

        <h2 id="lift-modal-title" className="lift-modal__title">
          LOG {CATEGORY_LABELS[category].toUpperCase()}
        </h2>

        <form className="lift-modal__form" onSubmit={handleSubmit}>
          <div className="lift-modal__fields">
            <div className="lift-modal__field">
              <label htmlFor="exerciseId">Exercise</label>
              <select
                id="exerciseId"
                ref={firstInputRef}
                value={exerciseId}
                onChange={(e) => {
                  setExerciseId(e.target.value);
                  // Back to the new lift's own last-logged numbers.
                  setTypedWeight(null);
                  setTypedReps(null);
                }}
                disabled={isSubmitting}
              >
                {exercises.map((ex) => (
                  <option key={ex.id} value={ex.id}>
                    {ex.displayName}
                  </option>
                ))}
              </select>
              {errors.exerciseId && <span className="lift-modal__error">{errors.exerciseId}</span>}
            </div>

            <div className="lift-modal__row">
              <div className="lift-modal__field">
                <label htmlFor="weightLb">
                  {isBodyWeightLift ? 'Added weight (lbs)' : 'Weight (lbs)'}
                </label>
                <input
                  id="weightLb"
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={weightLb}
                  onChange={(e) => setTypedWeight(e.target.value)}
                  disabled={isSubmitting}
                  placeholder={isBodyWeightLift ? '0 for bodyweight' : 'e.g. 135'}
                  min={isBodyWeightLift ? '0' : '1'}
                  max="2000"
                  step="1"
                />
                {isPerDumbbell && (
                  <span className="lift-modal__hint">Individual dumbbell weight</span>
                )}
                {errors.weightLb && <span className="lift-modal__error">{errors.weightLb}</span>}
              </div>

              <div className="lift-modal__field">
                <label htmlFor="reps">Reps (max 10)</label>
                <input
                  id="reps"
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={reps}
                  onChange={(e) => setTypedReps(e.target.value)}
                  disabled={isSubmitting}
                  min="1"
                  max="10"
                  step="1"
                />
                {errors.reps && <span className="lift-modal__error">{errors.reps}</span>}
              </div>
            </div>

            <div className="lift-modal__field">
              <label htmlFor="workoutDate">Date</label>
              <input
                id="workoutDate"
                type="date"
                value={workoutDate}
                onChange={(e) => setWorkoutDate(e.target.value)}
                max={maxDate}
                disabled={isSubmitting}
              />
              {errors.workoutDate && (
                <span className="lift-modal__error">{errors.workoutDate}</span>
              )}
            </div>

            {/* Follows the exercise picker, so switching lift switches records. */}
            <LiftBests highlights={selectedHighlights} />
          </div>

          <div className="lift-modal__submit-area">
            <button type="submit" className="lift-modal__submit" disabled={isSubmitting}>
              {isSubmitting ? 'LOGGING...' : 'LOG LIFT'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
